import { ForwardedRef, forwardRef } from "react";
import { Chip, useTheme } from "@mui/material";
import { ChipProps } from "@mui/material/Chip/Chip";
import { CloseSmallIcon } from "src/foundations/icons";
import { styled } from "src/styles/fpp-theme/fppStyled";
import { SenecFPPTheme } from "src/styles/fpp-theme/theme.types";

export type SenecTagColor = "aquamarin" | "azur" | "beige" | "limegreen" | "primary" | "quinary" | "sunyellow" | "tarocco";

export interface SenecTagProps extends Omit<ChipProps, "color"> {
    tagColor?: SenecTagColor;
}

export const SenecTag = forwardRef<HTMLDivElement, SenecTagProps>(
    ({ tagColor = "primary", ...props }, ref: ForwardedRef<HTMLDivElement>) => {
        return <StyledTag {...props} ref={ref} tagColor={tagColor} deleteIcon={<CloseSmallIcon />} skipFocusWhenDisabled />;
    },
);

const StyledTag = styled(Chip, {
    shouldForwardProp: (prop) => prop !== "color" && prop !== "tagColor",
})<SenecTagProps>(({ size, tagColor, onClick }) => {
    const theme: SenecFPPTheme = useTheme();
    const disableHover = !!onClick;

    return {
        color: textColor(theme, tagColor),
        backgroundColor: backgroundColor(theme, tagColor),
        fontSize: size == "small" ? "12px" : "14px",
        "& .MuiChip-deleteIcon": {
            color: textColor(theme, tagColor),
        },
        "& .MuiChip-labelSmall": {
            paddingTop: 2,
        },
        "&.MuiButtonBase-root:hover, &.MuiButtonBase-root:focus": disableHover
            ? {}
            : {
                  color: theme.palette.white.main,
                  backgroundColor: hoverColor(theme, tagColor),
                  "& .MuiChip-deleteIcon": {
                      color: theme.palette.white.main,
                  },
              },
        "&:focus": disableHover
            ? {}
            : {
                  outline: `${theme.palette.azur60.main} solid 2px`,
                  outlineOffset: "2px",
              },
    };
});

const backgroundColor = (theme: SenecFPPTheme, tagColor?: SenecTagColor) => {
    switch (tagColor) {
        case "aquamarin":
            return theme.palette.aquamarine00.main;
        case "azur":
            return theme.palette.surfaceBrandSecondary1Muted.main;
        case "beige":
            return theme.palette.beige00.main;
        case "limegreen":
            return theme.palette.aquamarine00.main;
        case "quinary":
            return theme.palette.neutralGray00.main;
        case "sunyellow":
            return theme.palette.sunYellow00.main;
        case "tarocco":
            return theme.palette.tarocco00.main;
        default:
            return theme.palette.white.main;
    }
};

const hoverColor = (theme: SenecFPPTheme, tagColor?: SenecTagColor) => {
    switch (tagColor) {
        case "aquamarin":
            return theme.palette.aquamarine60.main;
        case "azur":
            return theme.palette.azur60.main;
        case "beige":
            return theme.palette.beige60.main;
        case "limegreen":
            return theme.palette.limeGreen60.main;
        case "sunyellow":
            return theme.palette.sunYellow60.main;
        case "tarocco":
            return theme.palette.tarocco60.main;
        default:
            return theme.palette.neutralGray60.main;
    }
};

const textColor = (theme: SenecFPPTheme, tagColor?: SenecTagColor) => {
    switch (tagColor) {
        case "aquamarin":
            return theme.palette.aquamarine80.main;
        case "azur":
            return theme.palette.azur80.main;
        case "beige":
            return theme.palette.beige80.main;
        case "limegreen":
            return theme.palette.limeGreen80.main;
        case "sunyellow":
            return theme.palette.sunYellow80.main;
        case "tarocco":
            return theme.palette.tarocco80.main;
        default:
            return theme.palette.neutralGray100.main;
    }
};
